/**
 * mapExtractedToSubject — translate the merged OCR output produced by
 * ``services/document_extraction.merge_extracted_data`` into the field
 * shape that ``SubjectFormFields`` consumes.
 *
 * The OCR layer is doc-centric (each prompt produces what its source
 * naturally exposes — ``fiscal_street`` for a CSF, ``street`` for a
 * comprobante, ``passport_number`` for a passport, etc.). The form is
 * subject-centric (one row per persona física). This helper bridges
 * the two without leaking OCR concerns into the form components.
 *
 * Single source of truth — REP, APO, and BO phases all call this so
 * fields land in the same place. If we later add a new doc with a new
 * field, we add ONE entry here and every subject phase picks it up.
 */

import composeFiscalAddress from "./composeFiscalAddress";

function val(v) {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "n/a") return "";
  return s;
}

function composeFullName(m) {
  const fn = val(m.first_name);
  const ps = val(m.paternal_surname);
  const ms = val(m.maternal_surname);
  const parts = [fn, ps, ms].filter(Boolean);
  return parts.join(" ");
}

function composeResidentialAddress(m) {
  // Comprobante uses unprefixed keys (street / ext_number / etc.).
  // If those are missing, fall back to the CSF fiscal_* fields via
  // composeFiscalAddress — for a natural person whose only address
  // came from the CSF that's the closest we have.
  const street    = val(m.street);
  const extNum    = val(m.ext_number);
  const intNum    = val(m.int_number);
  const neigh     = val(m.neighborhood);
  const muni      = val(m.municipality);
  const state     = val(m.state);
  const zip       = val(m.zip_code);

  if (street || extNum || neigh || muni || zip) {
    const segs = [];
    const streetLine = [street, extNum].filter(Boolean).join(" ");
    if (streetLine) segs.push(streetLine);
    if (intNum)     segs.push(`Int. ${intNum}`);
    if (neigh)      segs.push(`Col. ${neigh}`);
    if (muni)       segs.push(muni);
    if (state)      segs.push(state);
    if (zip)        segs.push(`CP ${zip}`);
    return segs.join(", ");
  }
  return composeFiscalAddress(m);
}

export default function mapExtractedToSubject(merged) {
  if (!merged || typeof merged !== "object") return {};

  const out = {};

  const fullName = composeFullName(merged);
  if (fullName) out.full_name = fullName;

  const birth = val(merged.birth_date);
  if (birth) out.birth_date = birth;

  const curp = val(merged.curp);
  if (curp) out.curp = curp.toUpperCase();

  const rfc = val(merged.rfc);
  if (rfc) out.rfc = rfc.toUpperCase();

  const gender = val(merged.gender);
  if (gender) out.gender = gender;

  // ID type & number — INE prompt exposes id_type, id_number (already
  // normalized in merge_extracted_data: voter_key/CIC → id_number,
  // passport sets document_type="Pasaporte"). The form uses id_type
  // and id_number directly.
  const idType = val(merged.id_type) || val(merged.document_type);
  if (idType) {
    // Normalize "INE/IFE" / "Pasaporte" / "ine_actual" → form value.
    const norm = idType.toLowerCase();
    if (norm.includes("pasaporte") || norm.includes("passport")) {
      out.id_type = "Pasaporte";
    } else if (norm.includes("ine") || norm.includes("ife")) {
      out.id_type = "INE/IFE";
    } else {
      out.id_type = idType;
    }
  }
  const idNumber = val(merged.id_number);
  if (idNumber) out.id_number = idNumber;

  const idExpiry = val(merged.expiry_date) || val(merged.id_expiry_date);
  if (idExpiry) out.id_expiry_date = idExpiry;

  const email = val(merged.email);
  if (email) out.email = email;

  const nationality = val(merged.nationality);
  if (nationality) out.nationality = nationality;

  const address = composeResidentialAddress(merged);
  if (address) out.address = address;

  return out;
}
